import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import DocumentUpload from "../../shared/DocumentUpload/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

export default function TransferCertificateCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      student_name: "",
      admission_no: "",
      tc_no: "",
      tc_file: null,
      isActive: "Active",
    },
    validationSchema: Yup.object({
      student_name: Yup.string().required("Student Name is required"),
      tc_file: isEdit ? Yup.mixed().nullable() : Yup.mixed().required("TC File is required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("student_name", values.student_name);
      formData.append("admission_no", values.admission_no || "");
      formData.append("tc_no", values.tc_no || "");
      formData.append("status", values?.isActive === "Active" ? "true" : "false");
      if (values.tc_file instanceof File) {
        formData.append("tc_file", values.tc_file);
      }

      try {
        let response;
        if (isEdit) {
          formData.append("_method", "PUT");
          response = await request.post("transfer-certificates/" + id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await request.post("transfer-certificates/create", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        if (response?.data?.status) {
          toast.success(
            isEdit ? "TC updated successfully!" : "TC created successfully!",
          );
          navigate("/admin/transfer-certificates");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`transfer-certificates/${id}`);
          const item = response.data.data;
          formik.setValues({
            student_name: item.student_name || "",
            admission_no: item.admission_no || "",
            tc_no: item.tc_no || "",
            tc_file: item.tc_file || null,
            isActive: item.status ? "Active" : "Inactive",
          });
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit TC" : "Create New TC"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/transfer-certificates"),
              }}
              primaryButton={{
                label: isEdit ? "Update TC" : "Create TC",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField name="student_name" label="Student Name" type="text" formik={formik} autoFocus />
                </Box>
                <Box className="form-field">
                  <TextField name="admission_no" label="Admission No." type="text" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField name="tc_no" label="TC No." type="text" formik={formik} />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <DocumentUpload name="tc_file" formik={formik} label="PDF, DOC, DOCX, JPG, PNG" />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}